import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { RiExternalLinkFill } from "@itandi/itandi-bb-ui";
import clsx from "clsx";
import React from "react";
import ReactLinkify from "react-linkify";
import type { Deal } from "~/models/Deal";
import type { Message, MessageReceiver } from "~/models/Message";
import { getSpeakerName, isShowReadMark } from "~/models/Message";
import { getShowImagePath as getShowImagePathForCompany } from "~/utils/api/deals/Message";
import { getShowImagePath as getShowImagePathForVendor } from "~/utils/api/vendors/deals/Message";
import { formatDate } from "~/utils/DateUtil";
import Styles from "./ChatMessageRow.module.scss";

type Props = DeepReadonly<{
	deal: Deal;
	message: Message;
	receiver: MessageReceiver;
}>;

export const ChatMessageRow: React.FC<Props> = ({
	deal,
	message,
	receiver
}: Props) => {
	const selfStyle = React.useMemo(
		() => (message.speaker === receiver ? Styles.Self : Styles.Partner),
		[receiver, message]
	);
	const titleName = React.useMemo(() => getSpeakerName(message), [message]);
	const showReadMark = React.useMemo(
		() => isShowReadMark(message, receiver),
		[message, receiver]
	);
	const showImagePath = React.useMemo(() => {
		const getMethod =
			receiver === "maintenance_company"
				? getShowImagePathForCompany
				: getShowImagePathForVendor;
		return getMethod(deal.id, message.id);
	}, [deal, message, receiver]);
	const componentDecorator = React.useCallback(
		(
			decoratedHref: string,
			decoratedText: string,
			key: number
		): React.ReactNode => (
			<a
				className={Styles.Link}
				href={decoratedHref}
				key={key}
				rel="noreferrer noopener"
				target="blank"
			>
				{decoratedText}
			</a>
		),
		[]
	);
	return (
		<div className={clsx(Styles.MessageRow, selfStyle)}>
			<div className={Styles.MessageArea}>
				{titleName !== null && (
					<div className={Styles.MessageTitle}>{titleName}</div>
				)}
				<div className={clsx(Styles.Message, selfStyle)}>
					{message.messageType === "text" && (
						<span>
							<ReactLinkify componentDecorator={componentDecorator}>
								{message.text}
							</ReactLinkify>
						</span>
					)}
					{message.messageType === "file" && (
						<span>
							<a
								className={Styles.FileLink}
								href={showImagePath}
								rel="noreferrer"
								target="_blank"
							>
								{message.text}
							</a>
							<RiExternalLinkFill className={Styles.LinkIcon} />
						</span>
					)}
				</div>
				<div className={Styles.MessageFooter}>
					{showReadMark ? <span>既読</span> : null}
					<span className={Styles.Datetime}>
						{formatDate(message.createdAt, true)}
					</span>
				</div>
			</div>
		</div>
	);
};
